import { Card, CardBody, Button } from '@heroui/react';
import React from 'react';
import { FiAlertTriangle, FiArrowLeft } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

interface ErrorActionProps {
  label: string;
  onPress: () => void;
  color?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'danger'
    | 'default';
  variant?:
    | 'solid'
    | 'bordered'
    | 'light'
    | 'flat'
    | 'faded'
    | 'shadow'
    | 'ghost';
  startContent?: React.ReactNode;
  isLoading?: boolean;
}

interface ErrorCardProps {
  title: string;
  message: string;
  backLinkTo?: string;
  backLinkText?: string;
  onRetry?: () => void;
  action?: ErrorActionProps | React.ReactNode;
  className?: string;
}

export const ErrorCard: React.FC<ErrorCardProps> = ({
  title,
  message,
  backLinkTo,
  backLinkText = 'Go Back',
  onRetry,
  action,
  className = '',
}) => {
  const navigate = useNavigate();

  const handleBackClick = () => {
    if (backLinkTo) {
      navigate(backLinkTo);
    } else {
      window.history.back();
    }
  };

  return (
    <div className={`flex items-center justify-center min-h-96 ${className}`}>
      <Card className="max-w-md w-full bg-content1 border-danger-200">
        <CardBody className="text-center space-y-4 p-8">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-danger-100 rounded-full flex items-center justify-center">
              <FiAlertTriangle className="h-8 w-8 text-danger-600" />
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="text-xl font-semibold text-foreground">{title}</h3>
            <p className="text-default-600 text-sm leading-relaxed">
              {message}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            {/* Custom action */}
            {action && (
              <>
                {React.isValidElement(action) ? (
                  action
                ) : (
                  <Button
                    color={(action as ErrorActionProps).color || 'primary'}
                    variant={(action as ErrorActionProps).variant || 'solid'}
                    startContent={(action as ErrorActionProps).startContent}
                    isLoading={(action as ErrorActionProps).isLoading}
                    onPress={(action as ErrorActionProps).onPress}
                    className="flex-1"
                  >
                    {(action as ErrorActionProps).label}
                  </Button>
                )}
              </>
            )}

            {/* Retry action */}
            {onRetry && (
              <Button color="primary" onPress={onRetry} className="flex-1">
                Try Again
              </Button>
            )}

            {/* Back action - only show if no custom action provided */}
            {!action && (
              <Button
                variant="flat"
                color="default"
                startContent={<FiArrowLeft className="h-4 w-4" />}
                onPress={handleBackClick}
                className="flex-1"
              >
                {backLinkText}
              </Button>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};
